import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  DatePicker
} from '@mui/x-date-pickers/DatePicker';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import axiosInstance from '../utils/axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

const CourierDetailStats = () => {
  const { courierId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(location.state?.endDate || null);
  const [showNotFound, setShowNotFound] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (startDate) {
        params.append('startDate', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        params.append('endDate', format(endDate, 'yyyy-MM-dd'));
      }
      if (showNotFound) {
        params.append('showNotFound', 'true');
      }

      const response = await axiosInstance.get(`/courier-stats/${courierId}?${params.toString()}`);
      setData(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [courierId, startDate, endDate, showNotFound]);

  const handleDateFilter = () => {
    fetchStats();
  };

  const handleClearFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setTimeout(fetchStats, 100);
  };

  const getStatusColor = (status) => {
    return status === 'successful' ? 'success' : 'error';
  };

  const getStatusText = (status) => {
    return status === 'successful' ? 'Успешно' : 'Ненаход';
  };

  if (loading && !data) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>
          Загрузка детальной статистики курьера...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Хлебные крошки */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/courier-stats')}
          sx={{ textDecoration: 'none' }}
        >
          Статистика курьеров
        </Link>
        <Typography color="text.primary">
          {data?.courier?.name || 'Детали курьера'}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/courier-stats')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          Статистика курьера: {data?.courier?.name}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Фильтры по датам */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Фильтры
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Дата начала"
              value={startDate}
              onChange={setStartDate}
              renderInput={(params) => <TextField {...params} fullWidth />}
              format="dd.MM.yyyy"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Дата окончания"
              value={endDate}
              onChange={setEndDate}
              renderInput={(params) => <TextField {...params} fullWidth />}
              format="dd.MM.yyyy"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              onClick={handleDateFilter}
              disabled={loading}
              fullWidth
            >
              Применить
            </Button>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="outlined"
              onClick={handleClearFilter}
              disabled={loading}
              fullWidth
            >
              Сбросить
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {data && (
        <>
          {/* Информация о курьере */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Информация о курьере
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Имя:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {data.courier.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Username:
                </Typography>
                <Typography variant="body1">
                  @{data.courier.username}
                </Typography>
              </Grid>
              {data.courier.telegram && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Telegram:
                  </Typography>
                  <Typography variant="body1">
                    {data.courier.telegram}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Общая статистика */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Всего заказов
                      </Typography>
                      <Typography variant="h5" component="div">
                        {data.summary.totalOrders}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="h6">
                      {data.summary.successRate}%
                    </Typography>
                  </Box>
                  <Typography color="text.secondary">
                    Процент успеха
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CancelIcon sx={{ mr: 1, color: 'error.main' }} />
                    <Typography variant="h6">
                      {data.summary.notFoundOrders}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary">
                    Ненаходы
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="h6">
                      {data.summary.totalRevenue}₽
                    </Typography>
                  </Box>
                  <Typography color="text.secondary">
                    Общая выручка
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* График статистики по дням */}
          {data.dailyStats && data.dailyStats.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Статистика по дням
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.dailyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => format(new Date(value), 'dd.MM', { locale: ru })}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(value) => format(new Date(value), 'dd.MM.yyyy', { locale: ru })}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="totalOrders" 
                      stroke="#8884d8" 
                      name="Всего заказов"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="successfulOrders" 
                      stroke="#82ca9d" 
                      name="Успешные"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="notFoundOrders" 
                      stroke="#ff7c7c" 
                      name="Ненаходы"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          )}

          {/* Последние заказы */}
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {showNotFound ? 'Все ненаходы' : 'Последние заказы'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={!showNotFound ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setShowNotFound(false)}
                  disabled={loading}
                >
                  Последние заказы
                </Button>
                <Button
                  variant={showNotFound ? 'contained' : 'outlined'}
                  size="small"
                  color="error"
                  onClick={() => setShowNotFound(true)}
                  disabled={loading}
                >
                  Показать ненаходы
                </Button>
              </Box>
            </Box>
            
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>ID заказа</TableCell>
                    <TableCell>Дата</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Город</TableCell>
                    <TableCell>Район</TableCell>
                    <TableCell>Товар</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell align="right">Сумма</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.recentOrders.filter(order => showNotFound ? order.not_found : !order.not_found).map((order, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.orderId}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(order.date), 'dd.MM.yyyy HH:mm', { locale: ru })}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Chip
                          label={getStatusText(order.status)}
                          color={getStatusColor(order.status)}
                          size="small"
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {order.city || '-'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {order.district || '-'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {order.product || '-'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {order.type || '-'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {order.revenue}₽
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {data.recentOrders.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Нет заказов для отображения
                </Typography>
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default CourierDetailStats;
